/**
 * How a distance is written, in one place (N4).
 *
 * The twin of `backend/services/distance.py`, kept deliberately dull so the
 * two cannot disagree: **miles, one decimal, `mi` after the number.**
 *
 * FixyDany is a United States marketplace and a client comparing two plumbers
 * does not convert. Before this, three React files each decided for themselves
 * and decided the same wrong thing — miles for English, kilometres for
 * Spanish — so the Spanish interface, which is the one Daniel demonstrates in,
 * showed FixyBee cards at "16.8 km" next to an empty-area card that already
 * said "50 miles". The unit followed the interface language, and the
 * marketplace is not in the language, it is in the country.
 *
 * Deliberately NOT a translated unit. The Spanish interface shows US dollars
 * written the US way for the reason `money.js` gives, and distance is the same
 * decision: the number has to mean what a US address means by it.
 *
 * **The input is miles.** The conversion happens on the server, where the raw
 * value is (`earth_distance` gives metres, FixyMatch works in kilometres), so
 * nothing in the browser has to know which of the three it was handed.
 */

/** The outer edge of a trade call. Past it the city is more useful than the number. */
export const DISTANCE_LIMIT_MILES = 100;

/** `16.8` -> `16.8 mi`. Anything that is not a number returns `fallback`. */
export function milesText(value, fallback = "") {
  const n = typeof value === "string" ? Number(value) : value;
  if (n === null || n === undefined || typeof n !== "number" || !Number.isFinite(n)) {
    // A blank, never "0 mi": a FixyBee with no coordinates is at an unknown
    // distance, not at no distance.
    return fallback;
  }
  return `${n.toFixed(1)} mi`;
}

/**
 * The label a card shows, or null when it should show none.
 *
 * Null past `DISTANCE_LIMIT_MILES`, which is not a formatting rule but a
 * product one: a FixyBee 340 miles away is not "far", they are somewhere else,
 * and the card has a city on it that says so better.
 */
export function distanceLabel(miles) {
  const n = typeof miles === "string" ? Number(miles) : miles;
  if (n === null || n === undefined || typeof n !== "number" || !Number.isFinite(n)) {
    return null;
  }
  if (n > DISTANCE_LIMIT_MILES) return null;
  return milesText(n);
}
